import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../../api/axios';
import { IApiResponseError, IApiState } from '../../../../types/api';
import { RootState } from '../../..';
import { ISavingsBucket } from '../types';
import { getApiErrorMessage, getSavingsAuthConfig } from '../utils';

interface IUpdateBucketNameRequest {
  id: string;
  bucketName: string;
}

interface IUpdateBucketNameResponse {
  status?: boolean;
  message?: string;
  data?: ISavingsBucket;
  errors?: IApiResponseError[];
}

const initialState: IApiState<IUpdateBucketNameResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const updateBucketNameAsync = createAsyncThunk(
  'bucketName/update',
  async (
    { id, bucketName }: IUpdateBucketNameRequest,
    { rejectWithValue, getState },
  ) => {
    try {
      const state = getState() as RootState;

      const { data } = await api.patch<IUpdateBucketNameResponse>(
        `/savings/accounts/update-bucket-name/${id}`,
        { bucketName },
        getSavingsAuthConfig(state),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IUpdateBucketNameResponse>;
      return rejectWithValue(error.response?.data);
    }
  },
);

export const bucketNameUpdateSlice = createSlice({
  name: 'bucketName/update',
  initialState,
  reducers: {
    resetBucketNameUpdate(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateBucketNameAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(updateBucketNameAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IUpdateBucketNameResponse;
      })
      .addCase(updateBucketNameAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IUpdateBucketNameResponse;
        state.error = getApiErrorMessage(action.payload);
      });
  },
});

export const { resetBucketNameUpdate } = bucketNameUpdateSlice.actions;

export default bucketNameUpdateSlice.reducer;
